// doctor-frontend/src/services/auth.js
// Frontend auth helper used by LayoutWithSidebarAndHeader.js (dynamic import ../services/auth)
// Purpose:
// - Provide a single logout() function
// - Clear CMFM portal auth keys (and legacy keys) without clearing citizen portal keys (Option A)
// - Best-effort call to backend /api/auth/logout (and tenant-prefixed if available), but ignore failures
//
// NOTE: In JWT-based auth, "logout" is primarily client-side (clear token). Server may respond { ok: true }.

import http from './http';

// Canonical CMFM/staff-session keys. Citizen portal keys are intentionally
// excluded so a CMFM logout cannot destroy an independent citizen session.
const CMFM_SESSION_KEYS = [
  'cmfm_token',
  'cmfmToken',
  'authToken',
  'token',
  'role',
  'user',
  'userRole',
  'jwt',
  'accessToken',
  'portal_token',
  'portalToken',
  'tenant_token',
  'tenantToken',
  'cmfm_jwt',
];

/**
 * Clear a list of keys from both localStorage and sessionStorage.
 */
function clearKeys(keys) {
  try {
    keys.forEach((k) => {
      try { localStorage.removeItem(k); } catch {}
      try { sessionStorage.removeItem(k); } catch {}
    });
  } catch {}
}

/**
 * Attempt to POST to a logout endpoint, ignoring errors.
 * We try both non-tenant and tenant-prefixed variants:
 *  - /api/auth/logout
 *  - /t/:slug/api/auth/logout  (if app is currently under /t/:slug/...)
 */
async function bestEffortBackendLogout() {
  // Try via http service first (tenant-aware for /api paths in your setup)
  try {
    await http.post('/api/auth/logout', {});
    return;
  } catch {
    // ignore and try fallbacks below
  }

  // Direct fetch fallback (in case http base config differs)
  try {
    const base = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const path = '/api/auth/logout';
    await fetch(base + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({}),
      credentials: 'include',
    });
  } catch {
    // ignore
  }

  // Tenant-prefixed fallback if the current URL is under /t/:slug/...
  try {
    const p = (typeof window !== 'undefined' && window.location) ? String(window.location.pathname || '') : '';
    const m = p.match(/^\/t\/([^/]+)/);
    if (!m) return;
    const slug = m[1];
    const base = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const tenantPath = `/t/${encodeURIComponent(slug)}/api/auth/logout`;
    await fetch(base + tenantPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({}),
      credentials: 'include',
    });
  } catch {
    // ignore
  }
}

/**
 * Logout the current user.
 * This is safe to call even if no session exists.
 */
/**
 * Clear only the CMFM/staff session from this origin.
 *
 * This function is synchronous on purpose so a true 401 can invalidate a
 * stale CMFM token immediately without making another network request.
 * Citizen-specific keys (citizenToken, citizenAuth, citizenId, etc.) are not
 * touched here.
 */
export function clearCmfmSession() {
  clearLoggedInUserIfNonCitizen();
  clearKeys(CMFM_SESSION_KEYS);

  // Keep the shared HTTP helper aligned with storage. Its implementation also
  // preserves loggedInUser when that payload belongs to a citizen.
  try { http.clearUserToken?.(); } catch {}
}

export async function logout() {
  // Mark the explicit logout so ProtectedRoute does not race an expected 401
  // from the best-effort backend logout request.
  try { window.__cmfmLogoutInProgress = true; } catch {}

  // Start the optional server logout while the current token is still
  // available, but do not let a slow/expired server session delay client-side
  // logout. bestEffortBackendLogout handles its own failures.
  try { void bestEffortBackendLogout(); } catch {}

  // Client-side token removal is the authoritative logout for JWT sessions.
  clearCmfmSession();

  // Preserve the historical behavior of clearing owner credentials when they
  // happen to exist on the same origin, without touching citizen credentials.
  clearKeys(['ownerToken', 'ownerAuth']);
}

export default { logout, clearCmfmSession };
/**
 * Option A safeguard:
 * - Do NOT clear citizen portal session.
 * - If "loggedInUser" exists and is role === "citizen", keep it.
 * - Otherwise, remove it so CMFM logout works even for legacy flows that rely on loggedInUser.
 */
function clearLoggedInUserIfNonCitizen() {
  const maybeClear = (store) => {
    try {
      const raw = store.getItem('loggedInUser');
      if (!raw) return;
      let obj = null;
      try { obj = JSON.parse(raw); } catch { obj = null; }
      const core = obj?.user || obj;
      const role = String(core?.role || obj?.role || '').toLowerCase().trim();
      if (role === 'citizen') return; // keep citizen session
      store.removeItem('loggedInUser');
    } catch {}
  };
  maybeClear(localStorage);
  maybeClear(sessionStorage);
}


